import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  Button,
  DataDisplayTable,
  Dialog,
  DialogActions,
  DialogBodyText,
  LinkButton,
  StyledHeading,
  useDialog,
} from 'ui-kit';
import { IconNote } from '@tabler/icons-react';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import {
  decodeDealIdFromParam,
  formatCurrency,
  formatDate,
  formatPaymentMonth,
  formatPercentage,
  getBuyerCompanyIdFromDealId,
  getOfferingIdFromDealId,
  isValidDealId,
} from './utils';
import { RemittanceBackButton } from './RemittanceBackButton';
import NotesDialog from 'features/common-elements/NotesDialog';
import { GET_USER_COMPANY } from 'query/company';
import { GetUserCompany } from 'query/__generated__/company';
import {
  useGetUploadRemittanceRecords,
  DEFAULT_UPLOAD_PAGE_SIZE,
  type UploadRemittanceRecordRow,
  type RemittancesUploadStatus,
} from './getUploadRemittanceRecords_gql';
import {
  getApprovalBadgeClassName,
  APPROVAL_LABELS_REVIEW,
  type RemittanceUploadStatus,
} from './remittanceApprovalBadges';
import { useGetSingleMonthRemittanceRecords } from './getSingleMonthRemittanceRecords_gql';
import { useDealSeller } from './getDealSeller_gql';
import {
  useApproveRemittanceRecords,
  useRejectRemittanceRecords,
} from './approveRejectRemittance_gql';
import { useAddRemittanceNote, useRemittanceNotes } from './useRemittanceNotes';
import { useDealWireDetails } from './getDealWireDetails_gql';
import { useGetDealRemittanceSummaries } from './getDealRemittanceSummaries_gql';
import { RemittancePerformanceSummary } from './RemittancePerformanceSummary';
import useTableQueryParams from 'hooks/useTableQueryParams';
import {
  SortableField,
  SortDirection,
  VarianceCode,
  VarianceStatus,
} from '__generated__/globalTypes';
import {
  useGetAccountRemittanceVariances,
  type AccountVariance,
} from './getAccountRemittanceVariances_gql';
import VarianceNoteDialog from './VarianceNoteDialog';

const REMITTANCE_SORT_FIELD_MAP: Record<string, SortableField> = {
  account_id: SortableField.account_id,
  current_balance_cents: SortableField.current_balance_cents,
  original_balance_cents: SortableField.original_balance_cents,
  maturity_date: SortableField.maturity_date_seconds,
  days_past_due: SortableField.days_past_due,
  starting_balance_cents: SortableField.starting_balance_cents,
  payment_cents: SortableField.payment_cents,
  principal_cents: SortableField.principal_cents,
  interest_cents: SortableField.interest_cents,
  interest_rate_basis_points: SortableField.interest_rate_bps,
  charge_off: SortableField.charge_off,
  tdr: SortableField.tdr,
  bankruptcy: SortableField.bankruptcy,
};

const VARIANCE_CHIP_LABELS: Record<VarianceCode, string> = {
  [VarianceCode.BALANCE_INCREASED]: 'Balance ↑',
  [VarianceCode.BALANCE_EXCEEDS_ORIGINAL]: 'Bal > Orig',
  [VarianceCode.DPD_REVERSED]: 'DPD ↓',
  [VarianceCode.DPD_OUT_OF_RANGE]: 'DPD ⚠',
};

/** Small inline chip for variance codes in the datatable. Clicking opens the review
 *  modal focused on this specific code. Acknowledged variances render in a muted
 *  neutral; pending ones in pink to draw the seller's attention. */
const VarianceChip = ({
  code,
  acknowledged,
  onClick,
}: {
  code: VarianceCode;
  acknowledged: boolean;
  onClick: () => void;
}): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Open variance ${code}`}
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      acknowledged
        ? 'bg-slate-500/20 text-slate-300 hover:bg-slate-500/30'
        : 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-400/60 hover:bg-pink-500/30'
    }`}
  >
    {VARIANCE_CHIP_LABELS[code]}
  </button>
);

/** Badge config for review page header; same styling as upload history table. */
function getStatusBadge(
  uploadStatus: RemittancesUploadStatus | null,
): { label: string; className: string } | null {
  if (!uploadStatus) return null;
  const status = uploadStatus as RemittanceUploadStatus;
  return {
    label: APPROVAL_LABELS_REVIEW[status],
    className: getApprovalBadgeClassName(status),
  };
}

export const RemittanceTapeDealTable = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedNoteLoanData, setSelectedNoteLoanData] =
    useState<UploadRemittanceRecordRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { dealId: rawDealId, uploadId } = useParams<{ dealId?: string; uploadId?: string }>();
  const dealId = decodeDealIdFromParam(rawDealId);
  const noteDialog = useDialog();
  const approveConfirmDialog = useDialog();
  const rejectConfirmDialog = useDialog();
  const varianceDialog = useDialog();
  const openNoteDialog = useCallback(() => noteDialog.openDialog(), [noteDialog.openDialog]);
  // Store only the stable keys; derive the live AccountVariance objects via useMemo so
  // refetches propagate without a separate sync effect.
  const [varianceModalTarget, setVarianceModalTarget] = useState<{
    accountId: string;
    focusedCode: VarianceCode;
  } | null>(null);
  const offeringId = getOfferingIdFromDealId(dealId);
  // Notes are keyed server-side by (offering, buyerCompany, account). buyerCompany
  // must be the deal's buyer, not the viewer's company — otherwise the seller
  // hits ValidationError "buyerCompanyID does not match deal" in the resolver.
  const dealBuyerCompanyId = getBuyerCompanyIdFromDealId(dealId) ?? null;
  const { params, updateParams } = useTableQueryParams({ pageSize: DEFAULT_UPLOAD_PAGE_SIZE });

  useEffect(() => {
    setSorting([]);
    updateParams({ pageNumber: 1 });
  }, [uploadId]);

  const isNoteFetchEnabled =
    Boolean(offeringId) &&
    Boolean(dealBuyerCompanyId) &&
    noteDialog.isOpen &&
    Boolean(selectedNoteLoanData?.account_id);

  const { notes } = useRemittanceNotes({
    offeringID: offeringId ?? null,
    buyerCompanyID: dealBuyerCompanyId,
    accountID: selectedNoteLoanData?.account_id || null,
    resetKey: noteDialog.isOpen ? selectedNoteLoanData?.account_id || undefined : undefined,
    enabled: isNoteFetchEnabled,
  });
  const { addNote } = useAddRemittanceNote({
    offeringID: offeringId ?? null,
    buyerCompanyID: dealBuyerCompanyId,
    accountID: selectedNoteLoanData?.account_id || null,
  });

  // Deal IDs contain # (e.g. OFFERING#uuid#BUYER#uuid). If the user navigated with an unencoded
  // #, the path is truncated and the rest lands in window.location.hash. Reconstruct and replace
  // the URL so the rest of the app sees the full deal ID.
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash || !rawDealId || !dealId) return;
    if (isValidDealId(dealId)) return;
    const hashContent = hash.replace(/^#/, '');
    const reconstructed = `${decodeURIComponent(rawDealId)}#${hashContent}`;
    const decoded = decodeURIComponent(reconstructed);
    if (isValidDealId(decoded)) {
      const base = window.location.pathname;
      const dealSegment = `dealId/${encodeURIComponent(decoded)}`;
      const suffix = uploadId ? `/uploadId/${uploadId}` : '';
      const newPath = base.replace(/\/dealId\/[^/]+.*$/, `/${dealSegment}${suffix}`);
      navigate(newPath, { replace: true });
    }
  }, [rawDealId, dealId, uploadId, navigate]);

  const { data: userData } = useQuery<GetUserCompany>(GET_USER_COMPANY);
  const sellerCompanyId = useDealSeller(dealId);
  const userCompanyId = userData?.user?.company?.id ?? null;
  const isSeller = Boolean(sellerCompanyId && userCompanyId && sellerCompanyId === userCompanyId);

  const uploadSortParam = useMemo(() => {
    if (sorting.length === 0) return undefined;
    const col = sorting[0];
    const backendField = REMITTANCE_SORT_FIELD_MAP[col.id];
    if (!backendField) return undefined;
    return {
      sort_field: backendField,
      sort_direction: col.desc ? SortDirection.desc : SortDirection.asc,
    };
  }, [sorting]);

  const paginationParam = useMemo(
    () => ({
      page_size: params.pageSize,
      offset: (params.pageNumber - 1) * params.pageSize,
    }),
    [params.pageSize, params.pageNumber],
  );

  const {
    data: uploadData,
    loading: uploadLoading,
    error: uploadError,
    refetch: refetchUpload,
  } = useGetUploadRemittanceRecords(uploadId, {
    sort: uploadSortParam,
    pagination: paginationParam,
  });

  const {
    data: singleMonthData,
    loading: singleMonthLoading,
    error: singleMonthError,
  } = useGetSingleMonthRemittanceRecords(dealId, {
    skip: Boolean(uploadId),
  });

  const { listingName } = useDealWireDetails(dealId ?? undefined);
  const { data: summariesData, loading: summariesLoading } = useGetDealRemittanceSummaries(dealId);
  const summariesByPaymentMonth = summariesData?.summaries_by_payment_month ?? [];

  const [approveRemittance, { loading: approving }] = useApproveRemittanceRecords();
  const [rejectRemittance, { loading: rejecting }] = useRejectRemittanceRecords();

  const upload = uploadData?.getUploadRemittanceRecords;
  const singleMonth = singleMonthData?.getSingleMonthRemittanceRecords;

  const isUploadView = Boolean(uploadId);
  const entities = isUploadView ? (upload?.entities ?? []) : (singleMonth?.entities ?? []);

  // Variance hydration for the visible page. Scoped to the page's account IDs so we
  // don't drag the full upload's variance set into the client on every paging hop.
  // In upload view the upload_id comes from the URL; in single-month view every
  // returned record shares the month's resolved approved upload, so we can pull it
  // off the first entity.
  const pageAccountIds = useMemo(() => entities.map((e) => e.account_id), [entities]);
  const varianceUploadId = isUploadView
    ? uploadId
    : (singleMonth?.entities?.[0]?.upload_id ?? undefined);
  const { data: varianceData, refetch: refetchVariances } = useGetAccountRemittanceVariances(
    varianceUploadId,
    pageAccountIds,
  );
  const variancesByAccountId = useMemo(() => {
    const groups = varianceData?.getAccountRemittanceVariances?.variances_by_account ?? [];
    return new Map(groups.map((g) => [g.account_id, g.variances]));
  }, [varianceData]);

  // Derive live AccountVariance objects from the keys in varianceModalTarget so that
  // refetches automatically surface updated version/note/status to the open dialog.
  const resolvedVarianceTarget = useMemo(() => {
    if (!varianceModalTarget) return null;
    const variances = variancesByAccountId.get(varianceModalTarget.accountId) ?? [];
    const focused =
      variances.find((v) => v.variance_code === varianceModalTarget.focusedCode) ?? null;
    return { accountId: varianceModalTarget.accountId, variances, focused };
  }, [varianceModalTarget, variancesByAccountId]);

  const openVarianceDialog = (accountId: string, focused: AccountVariance) => {
    setVarianceModalTarget({ accountId, focusedCode: focused.variance_code });
    varianceDialog.openDialog();
  };

  const handleVarianceFocusChange = useCallback((code: VarianceCode) => {
    setVarianceModalTarget((prev) => (prev ? { ...prev, focusedCode: code } : prev));
  }, []);

  // variancesByAccountId is scoped to the current page. If the open modal's account
  // drops out of that scope (page change) or its focused code disappears server-side,
  // `focused` resolves to null and the dialog would render nothing while staying open —
  // an orphaned overlay with no close control. Close it instead. Apollo retains prior
  // `data` across the post-save refetch, so this does not fire during a normal save.
  useEffect(() => {
    if (varianceModalTarget && varianceDialog.isOpen && !resolvedVarianceTarget?.focused) {
      varianceDialog.setIsOpen(false);
      setVarianceModalTarget(null);
    }
  }, [varianceModalTarget, resolvedVarianceTarget, varianceDialog]);
  const totalCount = isUploadView ? (upload?.total_count ?? entities.length) : entities.length;
  const paymentMonth = entities[0]?.payment_month ?? null;
  const formattedPaymentMonth = formatPaymentMonth(paymentMonth);
  const uploadStatus = upload?.upload_status ?? null;
  const statusBadge = getStatusBadge(uploadStatus);
  const showAcceptReject = isSeller && uploadStatus === 'PENDING';

  const loading = isUploadView ? uploadLoading : singleMonthLoading;
  const error = isUploadView ? uploadError : singleMonthError;

  const handleApprove = async () => {
    if (!uploadId) return;
    setActionError(null);
    try {
      await approveRemittance({ variables: { input: { upload_id: uploadId } } });
      await refetchUpload();
      approveConfirmDialog.closeDialog();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Approve failed');
    }
  };

  const handleReject = async () => {
    if (!uploadId) return;
    setActionError(null);
    try {
      await rejectRemittance({ variables: { input: { upload_id: uploadId } } });
      await refetchUpload();
      rejectConfirmDialog.closeDialog();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Reject failed');
    }
  };

  const openApproveConfirm = () => approveConfirmDialog.openDialog();
  const openRejectConfirm = () => rejectConfirmDialog.openDialog();

  const columns: ColumnDef<UploadRemittanceRecordRow>[] = useMemo(
    () => [
      {
        id: 'account_id',
        accessorKey: 'account_id',
        header: 'Account ID',
        cell: (cellContext) => (
          <LinkButton
            text={cellContext.getValue() as string}
            onClick={() => {
              const accountId = cellContext.getValue() as string;
              const path = dealId
                ? `/portfolio/remittance/dealId/${encodeURIComponent(dealId)}/accountId/${encodeURIComponent(accountId)}`
                : `/portfolio/remittance/accountId/${encodeURIComponent(accountId)}`;
              navigate(path);
            }}
          />
        ),
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'current_balance_cents',
        accessorKey: 'current_balance_cents',
        header: 'UPB',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'original_balance_cents',
        accessorKey: 'original_balance_cents',
        header: 'O BAL',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'maturity_date',
        accessorKey: 'maturity_date',
        header: 'Maturity Date',
        cell: (cellContext) => {
          const value = cellContext.getValue() as string | null;
          return value ? formatDate(value) : '-';
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'interest_rate_basis_points',
        accessorKey: 'interest_rate_basis_points',
        header: 'Interest Rate',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatPercentage(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'accrued_unpaid_interest_cents',
        accessorKey: 'accrued_unpaid_interest_cents',
        header: 'Accrued Unpaid Interest',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: false,
      },
      {
        id: 'days_past_due',
        accessorKey: 'days_past_due',
        header: 'DPD',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? String(v) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'starting_balance_cents',
        accessorKey: 'starting_balance_cents',
        header: 'Starting BAL',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'payment_cents',
        accessorKey: 'payment_cents',
        header: 'Payment',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'principal_cents',
        accessorKey: 'principal_cents',
        header: 'Principal',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'interest_cents',
        accessorKey: 'interest_cents',
        header: 'Interest',
        cell: (cellContext) => {
          const v = cellContext.getValue() as number | null;
          return v != null ? formatCurrency(v / 100) : '-';
        },
        meta: { alignment: 'left', numeric: true },
        enableSorting: true,
      },
      {
        id: 'charge_off',
        accessorKey: 'charge_off',
        header: 'Charge-Off',
        cell: (cellContext) => {
          const v = cellContext.getValue() as boolean | null;
          return v != null ? (v ? 'Yes' : 'No') : '-';
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'tdr',
        accessorKey: 'tdr',
        header: 'TDR',
        cell: (cellContext) => {
          const v = cellContext.getValue() as boolean | null;
          return v != null ? (v ? 'Yes' : 'No') : '-';
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'bankruptcy',
        accessorKey: 'bankruptcy',
        header: 'Bankruptcy',
        cell: (cellContext) => {
          const v = cellContext.getValue() as boolean | null;
          return v != null ? (v ? 'Yes' : 'No') : '-';
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'variances',
        header: 'Variances',
        cell: (cellContext) => {
          const accountId = cellContext.row.original.account_id;
          const variances = variancesByAccountId.get(accountId) ?? [];
          if (variances.length === 0) return <span className="text-gray-600">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {variances.map((v) => (
                <VarianceChip
                  key={v.variance_code}
                  code={v.variance_code}
                  acknowledged={v.status === VarianceStatus.ACKNOWLEDGED}
                  onClick={() => openVarianceDialog(accountId, v)}
                />
              ))}
            </div>
          );
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: false,
      },
      {
        id: 'notes',
        header: 'Notes',
        // "Has notes" tint would need a bulk count query; until that exists
        // the icon stays neutral so we don't mislead readers with a state
        // that only populates after they've opened each loan once.
        cell: (cellContext) => (
          <button
            type="button"
            aria-label="Open notes"
            data-testid={`remittance-notes-button-${cellContext.row.original.account_id}`}
            className="text-gray-500 hover:text-gray-400"
            onClick={() => {
              setSelectedNoteLoanData(cellContext.row.original);
              openNoteDialog();
            }}
          >
            <IconNote size={20} stroke={1.5} />
          </button>
        ),
        meta: { alignment: 'left', numeric: false },
        enableSorting: false,
      },
    ],
    [dealId, navigate, openNoteDialog, variancesByAccountId],
  );

  const handleAddNote = (noteText: string) => {
    addNote(noteText);
  };
  const onSortingChanged = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const newSorting =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
    updateParams({ pageNumber: 1 });
  };

  if (!dealId) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={<RemittanceBackButton />}>
        <div className="rounded border border-gray-600 bg-background-canvas p-6 text-white">
          <p className="mb-2">Select an upload from Previous Remittance Tapes to view loans.</p>
          <Button
            size="small"
            label="Back to Remittance"
            onClick={() => navigate('/portfolio/remittance')}
          />
        </div>
      </PortfolioLayout>
    );
  }

  if (!isValidDealId(dealId)) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={<RemittanceBackButton />}>
        <div className="border-amber-600/50 text-amber-200 rounded border bg-background-canvas p-6">
          <p className="mb-2">
            Invalid deal ID in URL. Deal IDs must use the composite format (e.g. OFFERING#...
            #BUYER#...). Go back and open a deal from Serviced deals.
          </p>
          <Button
            size="small"
            label="Back to Remittance"
            onClick={() => navigate('/portfolio/remittance')}
          />
        </div>
      </PortfolioLayout>
    );
  }

  if (loading) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={<RemittanceBackButton />}>
        <div className="p-6 text-white">Loading remittance records…</div>
      </PortfolioLayout>
    );
  }

  if (error) {
    return (
      <PortfolioLayout hideSummery={true} summeryChildren={<RemittanceBackButton />}>
        <div className="rounded border border-red-500/50 bg-background-canvas p-6 text-red-400">
          Failed to load remittance records: {error.message}
        </div>
      </PortfolioLayout>
    );
  }

  return (
    <PortfolioLayout
      hideSummery={true}
      summeryChildren={
        <div>
          <RemittanceBackButton />
          <div className="mt-1 flex items-start gap-6">
            <div className="flex-shrink-0">
              <span className="text-sm font-normal text-gray-400">
                {isUploadView ? 'Remittance Upload for' : 'Remittance Accounts for'}
              </span>
              <div className="mt-0.5 text-xl font-semibold text-white">
                {listingName ?? dealId ?? '…'}
              </div>
              {formattedPaymentMonth && (
                <div className="mt-0.5 text-sm text-gray-400">
                  Remittances as of {formattedPaymentMonth}
                </div>
              )}
            </div>
            <RemittancePerformanceSummary
              summariesByPaymentMonth={summariesByPaymentMonth}
              loading={summariesLoading}
              paymentMonth={paymentMonth}
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              {statusBadge && (
                <span className={statusBadge.className} aria-label={statusBadge.label}>
                  {statusBadge.label}
                </span>
              )}
              {isUploadView && showAcceptReject && (
                <div className="flex gap-2">
                  <Button
                    size="small"
                    type="primary_green"
                    label="Approve"
                    disabled={approving || rejecting}
                    onClick={openApproveConfirm}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    type="secondary_light"
                    label="Reject"
                    disabled={approving || rejecting}
                    onClick={openRejectConfirm}
                    className="!border-red-500 !bg-red-600 !text-white hover:!border-red-600 hover:!bg-red-700"
                  >
                    Reject
                  </Button>
                </div>
              )}
            </div>
            {!isUploadView && (
              <Button
                size="small"
                onClick={() =>
                  navigate(`/portfolio/remittance/dealId/${encodeURIComponent(dealId)}/cashflow`)
                }
              >
                View Cashflow Profile
              </Button>
            )}
          </div>
          {actionError && (
            <div className="mt-2 text-sm text-red-400" role="alert">
              {actionError}
            </div>
          )}
        </div>
      }
    >
      <DataDisplayTable
        // @ts-expect-error - table expects __typename/last_updated_date; we pass upload record rows
        data={entities}
        // @ts-expect-error - column def type vs table generic
        columns={columns}
        noDataMessage={
          isUploadView
            ? 'No remittance records for this upload'
            : 'No remittance data for this deal. Upload a tape or select an upload from Previous Remittance Tapes.'
        }
        sorting={{ state: sorting, onSortingChanged, isManual: isUploadView }}
        pagination={
          isUploadView ? { queryParams: params, updateParams, totalItems: totalCount } : undefined
        }
        isDraggable={false}
        canResize={true}
      />
      {selectedNoteLoanData && (
        <NotesDialog
          dialog={noteDialog}
          documentName={`Loan ${selectedNoteLoanData.account_id}`}
          notes={notes}
          handleAddNote={handleAddNote}
        />
      )}
      {resolvedVarianceTarget && (
        <VarianceNoteDialog
          dialog={varianceDialog}
          accountId={resolvedVarianceTarget.accountId}
          variances={resolvedVarianceTarget.variances}
          focusedVariance={resolvedVarianceTarget.focused}
          onFocusChange={handleVarianceFocusChange}
          onSaved={async () => {
            await refetchVariances();
          }}
        />
      )}
      <Dialog dialog={approveConfirmDialog}>
        <StyledHeading className="mb-0">Confirm approval</StyledHeading>
        <DialogBodyText className="mx-auto max-w-md text-center">
          Approve this remittance upload for reporting?
        </DialogBodyText>
        <DialogActions>
          <Button
            size="medium"
            type="secondary_light"
            label="Cancel"
            onClick={() => approveConfirmDialog.closeDialog()}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            type="primary_green"
            label="Approve"
            onClick={() => void handleApprove()}
            disabled={approving || rejecting}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog dialog={rejectConfirmDialog}>
        <StyledHeading className="mb-0">Confirm rejection</StyledHeading>
        <DialogBodyText className="mx-auto max-w-md text-center">
          Reject this remittance upload? It will not be used for reporting.
        </DialogBodyText>
        <DialogActions>
          <Button
            size="medium"
            type="secondary_light"
            label="Cancel"
            onClick={() => rejectConfirmDialog.closeDialog()}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            type="secondary_light"
            label="Reject"
            onClick={() => void handleReject()}
            disabled={approving || rejecting}
            className="!border-red-500 !bg-red-600 !text-white hover:!border-red-600 hover:!bg-red-700"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </PortfolioLayout>
  );
};

export default RemittanceTapeDealTable;
